import React, { useEffect, useState } from 'react';
import { pageMetadata, companyInfo } from '../data';

export default function ContactPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
    website_url: '' // Honeypot field
  });

  const [status, setStatus] = useState({
    type: '', // 'success', 'error', ''
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    // Set page title and meta description
    document.title = pageMetadata.contact.title;
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute('content', pageMetadata.contact.description);
  }, []);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setStatus({ type: '', message: '' });

    // Client-side validation
    if (!formData.name || !formData.email || !formData.phone || !formData.message) {
      setStatus({
        type: 'error',
        message: 'Venligst udfyld alle påkrævede felter.'
      });
      setSubmitting(false);
      return;
    }

    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        const data = await response.json();
        if (data.status === 'ok') {
          setStatus({
            type: 'success',
            message: 'Mange tak for din besked! Jeg vender tilbage til dig hurtigst muligt.'
          });
          setFormData({
            name: '',
            email: '',
            phone: '',
            message: '',
            website_url: ''
          });
        } else {
          throw new Error('Fejl ved indsendelse.');
        }
      } else {
        throw new Error('Fejl ved indsendelse.');
      }
    } catch (error) {
      setStatus({
        type: 'error',
        message: 'Der opstod en fejl ved afsendelse af formularen. Prøv venligst igen senere eller skriv direkte til eis.johanne@gmail.com.'
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 space-y-16">
      {/* Header */}
      <div className="text-center max-w-3xl mx-auto space-y-4">
        <span className="text-brand-green text-xs font-semibold tracking-widest uppercase font-sans">Kontakt mig</span>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl text-brand-dark tracking-wide font-display">
          Når du er klar – er jeg her
        </h1>
        <p className="text-brand-muted font-sans text-base sm:text-lg">
          Du er altid velkommen til at kontakte mig, hvis du har spørgsmål til sessioner, events, ceremonier eller magiske redskaber.
        </p>
      </div>

      {/* Main Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
        {/* Contact Info Column (4 cols) */}
        <div className="lg:col-span-4 space-y-8 text-left bg-brand-sand/30 p-8 rounded-3xl border border-brand-sand">
          <h3 className="font-display text-2xl text-brand-dark tracking-wide">Kontaktoplysninger</h3>
          
          <div className="space-y-6 font-sans text-brand-muted text-sm sm:text-base">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-full bg-brand-cream border border-brand-beige flex items-center justify-center text-brand-green shrink-0">
                <i className="fa-light fa-location-dot text-lg" />
              </div>
              <div>
                <span className="block text-xs uppercase tracking-wider text-brand-muted/70 mb-0.5">Adresse</span>
                <span className="font-medium text-brand-dark">{companyInfo.address}</span>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-full bg-brand-cream border border-brand-beige flex items-center justify-center text-brand-green shrink-0">
                <i className="fa-light fa-phone text-lg" />
              </div>
              <div>
                <span className="block text-xs uppercase tracking-wider text-brand-muted/70 mb-0.5">Telefon</span>
                <a 
                  href={`tel:${companyInfo.phone.replace(/\s+/g, '')}`} 
                  className="font-medium text-brand-dark hover:text-brand-green transition-colors duration-300 whitespace-nowrap"
                >
                  {companyInfo.phone}
                </a>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-full bg-brand-cream border border-brand-beige flex items-center justify-center text-brand-green shrink-0">
                <i className="fa-light fa-envelope text-lg" />
              </div>
              <div>
                <span className="block text-xs uppercase tracking-wider text-brand-muted/70 mb-0.5">E-mail</span>
                <a 
                  href={`mailto:${companyInfo.email}`} 
                  className="font-medium text-brand-dark hover:text-brand-green transition-colors duration-300 whitespace-nowrap"
                >
                  {companyInfo.email}
                </a>
              </div>
            </div>

            <div className="pt-4 border-t border-brand-sand flex justify-between text-xs text-brand-muted/70">
              <span>CVR: {companyInfo.cvr}</span>
              <span>Sprog: Dansk</span>
            </div>
          </div>
        </div>

        {/* Form Column (8 cols) */}
        <div className="lg:col-span-8 bg-brand-cream p-8 sm:p-10 rounded-3xl border border-brand-sand shadow-sm text-left space-y-6">
          <h3 className="font-display text-2xl text-brand-dark tracking-wide">Send en besked</h3>
          
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Honeypot Field - Absolutely hidden from humans */}
            <div className="sr-only absolute -top-9999 -left-9999">
              <label htmlFor="website_url">Honeypot</label>
              <input
                id="website_url"
                type="text"
                name="website_url"
                value={formData.website_url}
                onChange={handleChange}
                autoComplete="off"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {/* Name */}
              <div className="space-y-2">
                <label htmlFor="name" className="block text-xs font-semibold uppercase tracking-wider text-brand-dark/80 font-sans">
                  Navn <span className="text-brand-rose">*</span>
                </label>
                <input
                  id="name"
                  type="text"
                  name="name"
                  required
                  value={formData.name}
                  onChange={handleChange}
                  className="w-full px-4 py-3 rounded-xl border border-brand-sand bg-brand-cream focus:outline-none focus:ring-2 focus:ring-brand-green/30 focus:border-brand-green transition-all duration-300 font-sans text-brand-dark"
                  placeholder="Dit fulde navn"
                />
              </div>

              {/* Email */}
              <div className="space-y-2">
                <label htmlFor="email" className="block text-xs font-semibold uppercase tracking-wider text-brand-dark/80 font-sans">
                  E-mail <span className="text-brand-rose">*</span>
                </label>
                <input
                  id="email"
                  type="email"
                  name="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full px-4 py-3 rounded-xl border border-brand-sand bg-brand-cream focus:outline-none focus:ring-2 focus:ring-brand-green/30 focus:border-brand-green transition-all duration-300 font-sans text-brand-dark"
                  placeholder="din@email.dk"
                />
              </div>
            </div>

            {/* Phone */}
            <div className="space-y-2">
              <label htmlFor="phone" className="block text-xs font-semibold uppercase tracking-wider text-brand-dark/80 font-sans">
                Telefonnummer <span className="text-brand-rose">*</span>
              </label>
              <input
                id="phone"
                type="tel"
                name="phone"
                required
                value={formData.phone}
                onChange={handleChange}
                className="w-full px-4 py-3 rounded-xl border border-brand-sand bg-brand-cream focus:outline-none focus:ring-2 focus:ring-brand-green/30 focus:border-brand-green transition-all duration-300 font-sans text-brand-dark"
                placeholder="Dit telefonnummer"
              />
            </div>

            {/* Message */}
            <div className="space-y-2">
              <label htmlFor="message" className="block text-xs font-semibold uppercase tracking-wider text-brand-dark/80 font-sans">
                Beskrivelse <span className="text-brand-rose">*</span>
              </label>
              <textarea
                id="message"
                name="message"
                required
                rows="5"
                value={formData.message}
                onChange={handleChange}
                className="w-full px-4 py-3 rounded-xl border border-brand-sand bg-brand-cream focus:outline-none focus:ring-2 focus:ring-brand-green/30 focus:border-brand-green transition-all duration-300 font-sans text-brand-dark resize-none"
                placeholder="Skriv din besked her..."
              />
            </div>

            {/* Status Messages */}
            {status.type && (
              <div className={`p-4 rounded-xl text-sm font-sans ${
                status.type === 'success' 
                  ? 'bg-emerald-50 text-emerald-800 border border-emerald-200' 
                  : 'bg-rose-50 text-rose-800 border border-rose-200'
              }`}>
                {status.message}
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={submitting}
              className="w-full bg-brand-green hover:bg-brand-green/90 disabled:bg-brand-green/50 text-brand-cream py-4 rounded-full text-base font-medium tracking-wide transition-all duration-300 shadow-md hover:shadow-lg flex items-center justify-center gap-2 cursor-pointer"
            >
              {submitting ? (
                <>
                  <i className="fa-light fa-spinner animate-spin" />
                  <span>Sender...</span>
                </>
              ) : (
                <>
                  <span>Send besked</span>
                  <i className="fa-light fa-paper-plane" />
                </>
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
